<script setup>
import { ref, computed, onBeforeUnmount, watch }    from 'vue'
import { useRouter }                                from 'vue-router'
import { useTranslaterStore }                       from '@/stores/translater'
import { useCertificatesStore }                     from '@/stores/certificates'
import UTAModal                                     from '@/components/UTAModal.vue'
import UTACertificatesCreate                        from '@/components/UTACertificatesCreate.vue'

const router = useRouter()

const translaterStore   = useTranslaterStore()
const certificatesStore = useCertificatesStore()

const showCreateCertificate = ref(false)
const showDeleteCertificate = ref(false) 
const deleteCertificateIMEI = ref('') 
const inputSearch           = ref('')
const pageNumber            = ref(1)

const getTranslate          = computed(() => translaterStore.getTranslate)
const certificates          = computed(() => certificatesStore.certificates)
const certificatesProcess   = computed(() => certificatesStore.certificatesProcess)
const certificatesMsg       = computed(() => certificatesStore.certificatesMsg)
const certificatesCreateMsg = computed(() => certificatesStore.certificatesCreateMsg)
const certificatesDeleteMsg = computed(() => certificatesStore.certificatesDeleteMsg)

function changePage(cursor, dir) {

    const params = {
        'c.limit': 12, 
        'c.direction': dir, 
        'c.cursor': cursor
    }

    if(inputSearch.value) 
        params['c.q'] = inputSearch.value

    certificatesStore.readCertificates(params).then(() => {
        if(certificates.value.array) {
            dir == 'after' ? 
                pageNumber.value++ : 
                pageNumber.value--
        } 
    })
}

certificatesStore.readCertificates({ 'c.limit': 12 })

onBeforeUnmount(() => {
    certificatesStore.setCertificates({})
    certificatesStore.setCertificatesMsg({})
    certificatesStore.setCertificatesCreateMsg({})
})

watch(inputSearch, input => {
    input.length > 2 ? 
        certificatesStore.readCertificates({ 'c.limit': 12, 'c.q': inputSearch.value }) :
        certificatesStore.readCertificates({ 'c.limit': 12 })
})

watch(certificatesCreateMsg, msg => {
    if('success' in msg && msg.success) {
        showCreateCertificate.value = false
        certificatesStore.readCertificates({ 'c.limit': 12 }).then(() => pageNumber.value = 1)
        router.push('/certificates/' + msg.data.imei)
    }
})

watch(certificatesDeleteMsg, msg => {
    if('success' in msg && msg.success) {
        showDeleteCertificate.value = false
        certificatesStore.readCertificates({ 'c.limit': 12 }).then(() => pageNumber.value = 1)
    }
})

watch(showCreateCertificate, mode => {
    if(mode) {
        certificatesStore.setCertificatesCreateMsg({})
    }
})

watch(showDeleteCertificate, mode => {
    if(mode) {
        certificatesStore.setCertificatesDeleteMsg({})
    }
})

</script>

<template>

    <div class="my-4 uta-certificates">

        <div class="container-fluid">

            <header class="uta-header" role="heading" aria-level="1">
                <h1 class="uta-header__title">{{ getTranslate.CERTIFICATES }}</h1>
                <div class="spinner-border uta-header__spinner" role="status" v-if="certificatesProcess">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <div class="uta-header__actions">
                    <a 
                        href="#" 
                        class="uta-btn uta-btn--main uta-btn--sm uta-btn--has-icon uta-header__action uta-header__action--create"
                        @click="showCreateCertificate = true"
                    >
                        <span class="uta-btn__content">
                            <span class="uta-btn__icon material-icons">
                                add
                            </span>
                            <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_ADD }}</span>
                        </span>
                    </a>
                </div>
            </header>

            <div 
                v-if="'success' in certificatesMsg && !certificatesMsg.success"
                class="mb-4 uta-alert uta-alert--danger"
            >   
                <span class="material-icons uta-alert__icon">
                    error_outline
                </span>
                <div class="uta-alert__text">
                    {{ getTranslate[certificatesMsg.text] || certificatesMsg.text }}
                </div>
            </div>

            <div 
                v-if="'success' in certificatesCreateMsg && certificatesCreateMsg.success"
                class="mb-4 uta-alert uta-alert--success"
            >   
                <span class="material-icons uta-alert__icon">
                    check_circle_outline
                </span>
                <div class="uta-alert__text">
                    {{ getTranslate[certificatesCreateMsg.text] || certificatesCreateMsg.text }}
                </div>
                <button type="button" class="uta-alert__close" @click="certificatesStore.setCertificatesCreateMsg({})">
                    <span class="material-icons">close</span>
                </button>
            </div>

            <div 
                v-if="'success' in certificatesDeleteMsg && certificatesDeleteMsg.success"
                class="mb-4 uta-alert uta-alert--success"
            >   
                <span class="material-icons uta-alert__icon">
                    check_circle_outline
                </span>
                <div class="uta-alert__text">
                    {{ getTranslate[certificatesDeleteMsg.text] || certificatesDeleteMsg.text }}
                </div>
                <button type="button" class="uta-alert__close" @click="certificatesStore.setCertificatesDeleteMsg({})">
                    <span class="material-icons">close</span>
                </button>
            </div>

            <div class="mb-4 justify-content-between">
                <div class="uta-input-group uta-input__wrapper uta-search">
                    <input 
                        type="text" 
                        class="form-control uta-input__wrapper-input uta-search__input"
                        :placeholder="getTranslate.SEARCH + '...'" 
                        v-model="inputSearch"
                        @keyup.escape="inputSearch = ''"
                    >
                    <template v-if="inputSearch.length > 2">
                        <span class="material-icons uta-input__wrapper-icon" @click="inputSearch = ''">
                            cancel
                        </span>
                    </template>
                    <template v-else>
                        <span class="material-icons uta-input__wrapper-icon">
                            search
                        </span>
                    </template>
                </div>
            </div>

            <div class="container-fluid" v-if="certificates.array">

                <div class="uta-list">

                    <div class="uta-list__title d-none d-lg-block">
                        <div class="row">
                            <div class="col-20 col-md-10">
                                <div class="row">
                                    <div class="col-12 col-lg-4">{{ getTranslate.CERTIFICATES_IMEI }}</div>
                                    <div class="col-12 col-lg-4">{{ getTranslate.CERTIFICATES_DATE_START }}</div>
                                    <div class="col-12 col-lg-4">{{ getTranslate.CERTIFICATES_DATE_STOP }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="uta-list__content">
                        <div 
                            class="row uta-list__item align-items-center" 
                            :class="{ 'uta-list__item--dark' : index % 2 }" 
                            v-for="(certificate, index) in certificates.array" 
                            :key="index"
                        >
                            <div class="col-10">
                                <div class="row">
                                    <div class="col-12 col-lg-4 uta-certificates__certificate-imei">
                                        <span class="d-inline d-lg-none">{{ getTranslate.CERTIFICATES_IMEI }}: </span>
                                        <router-link :to="'/certificates/' + certificate.imei" class="uta-list__item-link uta-certificates__link">
                                            {{ certificate.imei }}
                                        </router-link>
                                    </div>
                                    <div class="col-12 col-lg-4 uta-certificates__certificate-start">
                                        <span class="d-inline d-lg-none">{{ getTranslate.CERTIFICATES_DATE_START }}: </span>
                                        {{ new Date(Number(certificate.start) * 1000).toLocaleString() }}
                                    </div>
                                    <div class="col-12 col-lg-4 uta-certificates__certificate-stop">
                                        <span class="d-inline d-lg-none ">{{ getTranslate.CERTIFICATES_DATE_STOP }}: </span>
                                        {{ new Date(Number(certificate.stop) * 1000).toLocaleString() }}
                                    </div>
                                </div>
                            </div>
                            <div class="col-2 text-end">
                                <span class="material-icons uta-list__item-icon" @click="showDeleteCertificate = true, deleteCertificateIMEI = certificate.imei">
                                    delete
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="my-4 uta-certificates__pagination" v-if="certificates.c && (certificates.c.isBefore || certificates.c.isAfter)">
                    <div class="row">
                        <div class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3 col-xxl-2">
                            <div class="uta-pagination">
                                <button 
                                    :disabled="certificatesProcess || !certificates.c.isBefore"
                                    class="uta-btn uta-btn--sm uta-pagination__prev" 
                                    @click.prevent="changePage(certificates.c.start, 'before')"
                                >
                                    <span class="material-icons">navigate_before</span>
                                </button>
                                <div class="uta-pagination__current">
                                    {{ pageNumber }}
                                </div>
                                <button 
                                    :disabled="certificatesProcess || !certificates.c.isAfter"
                                    class="uta-btn uta-btn--sm uta-pagination__next" 
                                    @click.prevent="changePage(certificates.c.stop, 'after')"
                                >
                                    <span class="material-icons">navigate_next</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            <transition name="bounce">
                <UTAModal 
                    v-if="showCreateCertificate"
                    :header="
                        certificates.crt ? 
                            getTranslate.CERTIFICATES_UPDATE : 
                            getTranslate.CERTIFICATES_CREATE
                    "
                    size="lg"
                    @closeModal="showCreateCertificate = false"
                >
                    <template v-slot:body>
                        <UTACertificatesCreate 
                            :change="certificates.crt ? true : false"
                            @closeModal="showCreateCertificate = false" 
                        />
                    </template>
                </UTAModal>
            </transition>

            <transition name="bounce">
                <UTAModal 
                    v-if="showDeleteCertificate"
                    :header="getTranslate.CERTIFICATES_DELETE"
                    size="md"
                    @closeModal="showDeleteCertificate = false"
                >
                    <template v-slot:body>

                        <div 
                            v-if="'success' in certificatesDeleteMsg && !certificatesDeleteMsg.success"
                            class="uta-alert uta-alert--danger"
                        >   
                            <span class="material-icons uta-alert__icon">
                                error_outline
                            </span>
                            <div class="uta-alert__text">
                                {{ certificatesDeleteMsg.text }}
                            </div>
                            <button type="button" class="uta-alert__close" @click="certificatesStore.setCertificatesDeleteMsg({})">
                                <span class="material-icons">close</span>
                            </button>
                        </div>

                        {{ getTranslate.CERTIFICATES_DELETE_DESCR }}

                        <div class="mt-4 uta-btns">
                            <a
                                class="uta-btn" 
                                href="#" 
                                @click.prevent="
                                    showDeleteCertificate = false, 
                                    deleteCertificateIMEI = ''
                                "
                            >
                                {{ getTranslate.BTN_CANCEL }}
                            </a>
                            <a
                                class="uta-btn uta-btn--main" 
                                href="#" 
                                @click.prevent="certificatesStore.deleteCertificates({ imei: deleteCertificateIMEI })"
                            >
                                {{ getTranslate.BTN_DELETE }}
                            </a>
                        </div>

                    </template>
                </UTAModal>
            </transition>

        </div>

    </div>

</template>